/**
 * Problem + health fixtures.
 *
 * The problem bodies are built with the *server's* algorithm
 * (`acs_service/problems.py`): `type = https://acs.<domain>/problems/<code>`,
 * `title = code.replace('-', ' ').title()`, `status` from the catalogue, and
 * `instance = "<METHOD> <path>"`. That way the error renderer is exercised
 * against the bytes the façade really sends, not a pretty paraphrase.
 */

import type { Problem, ProblemCode } from '../api/types';

const PROBLEM_TYPES = 'https://acs.<domain>/problems/';

/** status → default detail, copied from the server catalogue. */
const BY_CODE: Record<ProblemCode, { status: number; detail: string }> = {
  'acs-unreachable': { status: 502, detail: 'upstream ACS/NBI could not be reached' },
  'device-not-found': { status: 404, detail: 'no device with this id is known to the ACS' },
  'device-offline': {
    status: 409,
    detail: 'device is offline; command rejected (fail-closed)',
  },
  'binding-conflict': { status: 409, detail: 'binding conflict: another binding exists' },
  'action-unsupported': { status: 422, detail: 'action not supported for this device' },
  'policy-conflict': {
    status: 409,
    detail: 'desired-state contradicts an existing enforced policy',
  },
  'job-timeout': {
    status: 409,
    detail: 'job did not complete within the maintenance window',
  },
  'job-not-found': { status: 404, detail: 'no job with this id is known to the ACS' },
  'contract-version-unsupported': { status: 409, detail: 'API version pin mismatch' },
  'stale-snapshot': { status: 409, detail: 'snapshot older than the freshness floor' },
  'reference-invalid': {
    status: 422,
    detail: 'an opaque cross-service reference is invalid',
  },
  'scope-insufficient': { status: 403, detail: 'client assertion lacks the required scope' },
  'rate-limited': { status: 429, detail: 'client over its per-minute budget' },
  'idempotency-conflict': {
    status: 409,
    detail: 'idempotency key reused with a different request',
  },
  // U-03-a: the status and the sentence are the server's own (`acs_service/problems.py::_BY_CODE`),
  // copied verbatim — `tests/test_acs_h8b_backup_doors.py` compares these statuses against the
  // catalogue, so a drift here is an operator seeing a success-shaped tone for a 409 fact.
  'idempotency-in-progress': {
    status: 409,
    detail: 'an identical request for this idempotency key is still running',
  },
  'signature-invalid': { status: 401, detail: 'client assertion signature failed to verify' },
  'olt-not-found': { status: 404, detail: 'no OLT with this id in the adapter inventory' },
  'adapter-unreachable': { status: 502, detail: 'OLT adapter could not be reached' },
  // U-05/C1d (AUD-05/G-OLT-13): the server catalogue's statuses and generic sentences,
  // verbatim — the drift guard in `tests/test_acs_h8b_backup_doors.py` compares them.
  'adapter-unconfigured': {
    status: 503,
    detail: 'this deployment has no configured OLT adapter link, so the adapter refuses to act',
  },
  'adapter-state-unavailable': {
    status: 503,
    detail:
      'the OLT adapter could not persist its state; the operation is idempotent and safe to retry',
  },
  // C9: the adapter answered and what it said does not match the request it was given.
  // The server catalogue's own status and sentence, verbatim — the drift guard in
  // `tests/test_acs_h8b_backup_doors.py` compares these against the catalogue.
  'adapter-answer-invalid': {
    status: 502,
    detail: "the OLT adapter's answer does not match the request it was given",
  },
  // G-PRV-05: the upstream refused the preset write — the server catalogue's own
  // status and sentence, verbatim.
  'acs-refused': {
    status: 502,
    detail: 'the upstream ACS/NBI answered this write with an error',
  },
  'firmware-not-found': {
    status: 404,
    detail: 'no firmware image with this id in the registry',
  },
  'campaign-not-found': { status: 404, detail: 'no firmware campaign with this id is known' },
  'alarm-not-found': {
    status: 404,
    detail: 'no event with this id in the outbox',
  },
  // ALM-05 (G-ALM-05): a cancel naming a window nobody planned — its own word,
  // because a window is a plan, not a device row.
  'maintenance-not-found': {
    status: 404,
    detail: 'no maintenance window with this id is known to the ACS',
  },
  // N5 (G-PRC-10): the incident doors' refusals — the server's own sentences.
  'incident-not-found': {
    status: 404,
    detail: 'no incident with this id is known',
  },
  'incident-already-closed': {
    status: 409,
    detail: 'this incident is already closed; its closure stands and is not repeated',
  },
  'alarm-already-in-incident': {
    status: 409,
    detail: 'this alarm is already attached to an open incident',
  },
  // N6 (G-ALM-08, webhook first): the notification channels' refusals — the server's
  // own sentences. The kind refusal is the owner-ruling sentence, not a 501.
  'notification-channel-not-found': {
    status: 404,
    detail: 'no notification channel with this id is known',
  },
  'channel-kind-unsupported': {
    status: 422,
    detail:
      "this channel kind is not built; only 'webhook' is accepted until the provider ruling is made",
  },
  'notification-url-invalid': {
    status: 422,
    detail: 'a notification channel URL must be an http(s) URL',
  },
  // N1/N2 (G-ALM-03/06): the alarm lifecycle's refusals, with the server's own statuses and
  // sentences (`acs_service/problems.py::_BY_CODE`) — the drift guard in
  // `tests/test_acs_h8b_backup_doors.py` compares these against the catalogue.
  'alarm-already-cleared': {
    status: 409,
    detail: 'this alarm is already closed; its closure stands and is not repeated',
  },
  'alarm-not-escalatable': {
    status: 409,
    detail: 'this alarm is closed; a closed record cannot be escalated',
  },
  'alarm-not-assignable': {
    status: 409,
    detail: 'this alarm is closed; a closed record cannot be assigned',
  },
  'alarm-not-ackable': {
    status: 409,
    detail: 'this alarm is closed; a closed record cannot be acked',
  },
  'actor-not-human': {
    status: 403,
    detail:
      "this door records a named person's responsibility; a machine assertion cannot stand in for one",
  },
  'report-not-found': {
    status: 404,
    detail: 'no reconciliation report with this run id',
  },
  // Circuit M and circuit T, with the server's status and detail verbatim.
  'capability-unproven': {
    status: 409,
    detail: 'no witness proves this capability on this model',
  },
  'operation-not-found': {
    status: 404,
    detail: 'not an operator operation of the shipped catalog',
  },
  'confirmation-required': {
    status: 409,
    detail: 'the typed confirmation string is not the one this write needs',
  },
  'manifest-not-found': {
    status: 404,
    detail: 'no manifest document of the loaded set names this model and slot',
  },
  'not-an-action': {
    status: 422,
    detail: 'this slot is a parameter, not an RPC, so there is no method to run',
  },
  'job-not-cancellable': {
    status: 409,
    detail: 'the job has already concluded; there is no wait left to stop',
  },
  // Circuit E (E2), with the server's status and detail verbatim. The one refusal that is about a
  // FILE rather than about the request: the declaration was well formed, the bytes are another image.
  'checksum-mismatch': {
    status: 422,
    detail: 'the bytes uploaded do not hash to the declared checksum_sha256',
  },
  // Circuit H4-b's dead-letter doors, verbatim from the server catalogue.
  'event-not-found': {
    status: 404,
    detail: 'no outbox delivery record with this event id',
  },
  'event-not-redrivable': {
    status: 409,
    detail: 'the row is not a dead letter, so there is no spent budget to reset',
  },
  // Circuit H's admin doors (H7-b, H8-b): the server's status and generic sentence verbatim. A test
  // in `tests/test_acs_h8b_backup_doors.py` compares this table's statuses with the server catalogue,
  // so a status invented here cannot drift from the one the door actually answers.
  // `backup-dir-unavailable` (409) and `backup-not-found` (404) are the same operator action with two
  // opposite causes — a mount, or a filename — and blurring them is how a DR check misdiagnoses.
  'migration-unrecorded': {
    status: 409,
    detail: 'this store has no recorded schema version — --baseline first',
  },
  'migration-ahead': {
    status: 409,
    detail: 'the schema version this store recorded is ahead of this build',
  },
  // DA-38's single-flight refusals, copied from the server catalogue: both say WHO won (a live
  // peer), not that the build is wrong — which is what makes them 409 conflicts, not errors.
  'migration-busy': {
    status: 409,
    detail: 'another migrator holds the migrations pass',
  },
  'migration-lost-race': {
    status: 409,
    detail: 'another migrator advanced the schema record while this one was running',
  },
  'backup-not-found': {
    status: 404,
    detail: 'no backup artifact of that name in the configured backup directory',
  },
  'backup-dir-unavailable': {
    status: 409,
    detail: 'the configured backup directory is not a readable directory',
  },
  // U-17 legal hold: the row's own state forbids a second lift (409) and an unknown
  // hold id is a lookup miss (404) — the same two-cause split `backup-*` draws.
  'legal-hold-not-found': {
    status: 404,
    detail: 'no legal hold with this id is known to the ACS',
  },
  'legal-hold-already-lifted': {
    status: 409,
    detail: 'this legal hold is already lifted; its lifting stands and is not repeated',
  },
  // R1's identity doors. The statuses are the server catalogue's, verbatim — the drift guard in
  // `tests/test_acs_h8b_backup_doors.py` compares them, so the login screen cannot be told a 503 is a
  // 401 or that a taken username is a malformed body.
  'auth-unconfigured': {
    status: 503,
    detail: 'this deployment has no active owner, so no session can be opened',
  },
  'user-not-found': { status: 404, detail: 'no console user with this id' },
  'user-exists': { status: 409, detail: 'a console user with this username already exists' },
  'last-owner': {
    status: 409,
    detail: 'this is the last active owner; it cannot be demoted, disabled or deleted',
  },
  // U-06's operator-profile doors. Statuses and generic sentences verbatim from the server
  // catalogue — the drift guard in `tests/test_acs_h8b_backup_doors.py` compares them, so a console
  // cannot render «re-read it» as a 422 or a refused release as a malformed body.
  'profile-invalid': {
    status: 422,
    detail: 'the candidate profile document does not pass validation',
  },
  'profile-stale-version': {
    status: 409,
    detail: 'another activation landed on this profile first; re-read it',
  },
  'profile-release-blocked': {
    status: 409,
    detail:
      "this deployment, today, will not activate these bytes: at `:validate`/`PUT` its only complaints are this deployment's release rules, and at `:rollback` a stored version no longer passes this build's validation",
  },
  // U-06-d: the status is the server catalogue's again, verbatim — 404, the lookup-miss family
  // `backup-not-found` and `user-not-found` belong to.
  'profile-version-not-found': {
    status: 404,
    detail: "no operator-profile version with this number in the store's history window",
  },
  // S1 (G-SUB-01/03): the subscriber surface's four refusals, with the server catalogue's status and
  // sentence VERBATIM — the drift guard in `tests/test_acs_h8b_backup_doors.py` compares these
  // statuses with `acs_service/problems.py::_BY_CODE`, so a console cannot tell a 503 «this
  // deployment does not run it» from a 404 «no such row».
  'subscriber-disabled': {
    status: 503,
    detail:
      'this deployment does not run the subscriber surface; set ACS_SUBSCRIBER_ENABLED=true and restart the API',
  },
  'subscriber-not-found': {
    status: 404,
    detail: 'no subscriber with this id is known to the ACS',
  },
  'service-profile-not-found': {
    status: 404,
    detail: 'no service profile with this id is known to the ACS',
  },
  'service-profile-conflict': {
    status: 409,
    detail: 'a service profile with this name already exists',
  },
  // S4 (G-SUB-05): the subscriber's lifecycle step's own refusal, with the server
  // catalogue's status and sentence VERBATIM — the drift guard in
  // `tests/test_acs_h8b_backup_doors.py` compares these statuses with
  // `acs_service/problems.py::_BY_CODE`.
  'subscriber-state-conflict': {
    status: 409,
    detail: "the subscriber's status is not the one this transition requires",
  },
};

export function problemCodeList(): readonly ProblemCode[] {
  return Object.keys(BY_CODE) as ProblemCode[];
}

/** Python `str.title()` on this vocabulary is equivalent to capitalising tokens. */
function titleOf(code: ProblemCode): string {
  return code
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface ProblemFixtureInit {
  detail?: string;
  instance?: string;
  /** 429 carries `Retry-After` as a header, never in the body. */
  retryAfterSeconds?: number;
}

export function buildProblem(code: ProblemCode, init: ProblemFixtureInit = {}): Problem {
  const base = BY_CODE[code];
  const problem: Problem = {
    type: `${PROBLEM_TYPES}${code}`,
    title: titleOf(code),
    status: base.status,
    detail: init.detail ?? base.detail,
    code,
  };
  problem.instance = init.instance ?? 'POST /integration/v1/devices/{id}/actions';
  return problem;
}

/** The scenarios the actions panel can replay on demand. */
export const PROBLEM_SCENARIOS: readonly {
  key: string;
  label: string;
  code: ProblemCode;
  problem: Problem;
  retryAfterSeconds?: number;
}[] = [
  {
    key: 'device-offline',
    label: 'دستگاه آفلاین (reboot روی ONU خاموش)',
    code: 'device-offline',
    problem: buildProblem('device-offline', {
      detail: 'acs-device:VSL554422B is offline; command rejected (fail-closed)',
      instance: 'POST /integration/v1/devices/acs-device:VSL554422B/actions',
    }),
  },
  {
    key: 'rate-limited',
    label: 'سقف نرخ connection-request (۴۲۹ + Retry-After)',
    code: 'rate-limited',
    problem: buildProblem('rate-limited', {
      detail: 'connection-request budget exhausted; retry later',
      instance: 'POST /integration/v1/devices/acs-device:HWTC481230A/actions',
    }),
    retryAfterSeconds: 12,
  },
  {
    key: 'idempotency-conflict',
    label: 'تکرار Idempotency-Key با بدنهٔ متفاوت',
    code: 'idempotency-conflict',
    problem: buildProblem('idempotency-conflict', {
      detail: 'key act:reboot:hwtc:0001 was already used with body {"action":"apply_policy"}',
      instance: 'POST /integration/v1/devices/acs-device:HWTC481230A/actions',
    }),
  },
  {
    key: 'scope-insufficient',
    label: 'اسکوپ کافی نیست (command:reboot)',
    code: 'scope-insufficient',
    problem: buildProblem('scope-insufficient', {
      detail: 'assertion scope "device:read event:read" lacks command:reboot',
      instance: 'POST /integration/v1/devices/acs-device:ZTEG205544C/actions',
    }),
  },
  {
    key: 'action-unsupported',
    label: 'عملیات پشتیبانی نمی‌شود (speedtest بدون TR-143)',
    code: 'action-unsupported',
    problem: buildProblem('action-unsupported', {
      detail: 'speedtest requires the "tr143" capability on this device doc',
      instance: 'POST /integration/v1/devices/acs-device:CDAT330911/diagnostics',
    }),
  },
  {
    key: 'stale-snapshot',
    label: 'اسنپ‌شات کهنه (stale-snapshot)',
    code: 'stale-snapshot',
    problem: buildProblem('stale-snapshot', {
      detail: 'snapshot captured 2026-10-08T02:11:00Z is older than the 6h floor',
      instance: 'GET /integration/v1/devices/acs-device:FHA778120/parameters',
    }),
  },
  {
    key: 'acs-unreachable',
    label: 'GenieACS/NBI در دسترس نیست (۵۰۲)',
    code: 'acs-unreachable',
    problem: buildProblem('acs-unreachable', {
      detail: 'NBI POST /devices/acs-device:ZTEG205544C/tasks timed out after 20s',
      instance: 'GET /integration/v1/devices/acs-device:ZTEG205544C',
    }),
  },
];

export function scenarioByKey(key: string) {
  return PROBLEM_SCENARIOS.find((s) => s.key === key);
}
